import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { UnresolvedEntriesAlert } from './UnresolvedEntriesAlert';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function markReviewed(type, id, note) {
  const endpoint = type === 'entry'
    ? `${API_URL}/api/time/unresolved-entries/${id}/mark-reviewed`
    : `${API_URL}/api/time/unresolved-adjustments/${id}/mark-reviewed`;
  const token = localStorage.getItem('token');
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note || '' }),
  });
  if (res.ok) {
    toast.success('Marked as reviewed');
    return true;
  }
  toast.error('Failed to mark reviewed');
  return false;
}

export function EmployeeDaysBreakdown({ data, formatDateDisplay, isAdmin = false, onReviewed }) {
  return (
    <div className="employee-hours-breakdown">
      {/* Payroll-sacred: surface any time records that need human review */}
      <UnresolvedEntriesAlert
        unresolvedEntries={data?.unresolved_entries || []}
        unresolvedAdjustments={data?.unresolved_adjustments || []}
        isAdmin={isAdmin}
        onMarkReviewed={isAdmin ? async (type, id, note) => {
          const ok = await markReviewed(type, id, note);
          if (ok && onReviewed) onReviewed();
        } : undefined}
      />

      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-green-800">
            {data?.employee_name || 'Employee'} Hours
          </h3>
          <div className="text-2xl font-bold text-green-600">
            {data?.total_hours ?? 0} hrs total
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(() => {
          // When we get more than one week (e.g. bi-weekly view), group days
          // under a "Week N" header so the user can see the split visually.
          const days = data?.days || [];
          const grouped = (data?.weeks && data.weeks > 1)
            ? days.reduce((acc, d) => {
                const k = d.week_index ?? 0;
                acc[k] = acc[k] || [];
                acc[k].push(d);
                return acc;
              }, {})
            : { 0: days };
          const totalWeeks = data?.weeks || 1;
          const labelFor = (wi) => {
            if (totalWeeks <= 1) return null;
            if (wi === totalWeeks - 1) return 'This week';
            if (wi === totalWeeks - 2) return 'Previous week';
            return `Week ${wi + 1}`;
          };
          const renderDay = (day, idx) => (
            <Card key={`${day.date}-${idx}`} className={day.is_today ? 'border-2 border-green-500 bg-green-50' : ''}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      {day.day}
                      {day.is_today && <Badge variant="success" className="text-xs">Today</Badge>}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDateDisplay ? formatDateDisplay(day.date) : day.date}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xl font-bold ${day.hours > 0 ? 'text-green-600' : day.hours < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {day.hours} hrs
                    </p>
                    {day.adjustment_total && day.adjustment_total !== 0 && (
                      <p className={`text-xs ${day.adjustment_total > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        ({day.adjustment_total > 0 ? '+' : ''}{day.adjustment_total} adjusted)
                      </p>
                    )}
                    {day.entries && day.entries.length > 0 && (
                      <p className="text-xs text-gray-500">{day.entries.length} shift(s)</p>
                    )}
                  </div>
                </div>
                {day.entries && day.entries.length > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    {day.entries.map((entry, entryIdx) => (
                      <div key={entryIdx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                        <span className="text-gray-600">
                          {new Date(entry.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                          {new Date(entry.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="font-medium flex items-center gap-2">
                          {entry.is_auto_clockout && (
                            <Badge variant="secondary" className="text-[10px]">auto</Badge>
                          )}
                          {entry.hours} hrs
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {day.adjustments && day.adjustments.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    {day.adjustments.map((adj, adjIdx) => (
                      <div key={adjIdx} className={`text-xs p-2 rounded ${adj.adjustment_hours > 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                        <span className="font-medium">{adj.adjustment_hours > 0 ? '+' : ''}{adj.adjustment_hours} hrs</span>
                        <span className="mx-1">•</span>
                        <span>{adj.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );

          const weekIndices = Object.keys(grouped).map(Number).sort((a, b) => a - b);
          return weekIndices.map((wi) => {
            const weekDays = grouped[wi];
            const weekTotal = weekDays.reduce((s, d) => s + (d.hours || 0), 0);
            const label = labelFor(wi);
            return (
              <div key={`week-${wi}`} className="space-y-3">
                {label && (
                  <div className="flex items-center justify-between bg-slate-100 border border-slate-200 rounded px-3 py-1.5 mt-3">
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                    <span className="text-sm font-bold text-slate-700">{weekTotal.toFixed(2)} hrs</span>
                  </div>
                )}
                {weekDays.map((d, idx) => renderDay(d, idx))}
              </div>
            );
          });
        })()}
        {(!data?.days || data.days.length === 0) && (
          <p className="text-center text-gray-500 py-8">No hours logged in this period</p>
        )}
      </div>
    </div>
  );
}

export function StatsDetailsDialog({ open, onOpenChange, detailsType, detailsData, detailsLoading, projects, materialOrders, employees, onViewOrderDetails, formatDateDisplay, isAdmin = true }) {
  // Drill-in state: when admin clicks an employee in weekly/biweekly breakdown, fetch their day-by-day data
  const [drillInEmployee, setDrillInEmployee] = useState(null); // { id, name }
  const [drillInData, setDrillInData] = useState(null);
  const [drillInLoading, setDrillInLoading] = useState(false);

  // Reset drill-in when dialog closes or detailsType changes
  useEffect(() => {
    if (!open) {
      setDrillInEmployee(null);
      setDrillInData(null);
    }
  }, [open]);

  useEffect(() => {
    setDrillInEmployee(null);
    setDrillInData(null);
  }, [detailsType]);

  const openDrillIn = async (employeeId, employeeName) => {
    if (!employeeId) return;
    const period = detailsType === 'weekly-hours' ? 'week' : 'biweek';
    setDrillInEmployee({ id: employeeId, name: employeeName });
    setDrillInLoading(true);
    setDrillInData(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/dashboard/employee-weekly-breakdown/${employeeId}?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDrillInData(await res.json());
      } else {
        setDrillInData({ days: [], total_hours: 0, employee_name: employeeName });
      }
    } catch {
      setDrillInData({ days: [], total_hours: 0, employee_name: employeeName });
    } finally {
      setDrillInLoading(false);
    }
  };

  const closeDrillIn = () => {
    setDrillInEmployee(null);
    setDrillInData(null);
  };

  const dialogTitle = drillInEmployee
    ? `${drillInEmployee.name}'s Daily Breakdown`
    : ({
        'projects': 'Active Projects Details',
        'clocked-in': 'Currently Clocked In',
        'weekly-hours': "This Week's Hours Breakdown",
        'biweekly-hours': 'Bi-Weekly Hours Breakdown',
        'biweekly-payroll': 'Bi-Weekly Payroll Breakdown',
        'open-orders': 'Open Orders - Not Completed',
        'personal-weekly-hours': 'Your Weekly Hours Breakdown',
        'personal-biweekly-hours': 'Your Bi-Weekly Hours Breakdown',
        'personal-biweekly-payroll': 'Your Bi-Weekly Hours Breakdown',
      }[detailsType] || '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="stats-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {drillInEmployee && (
              <Button variant="ghost" size="sm" onClick={closeDrillIn} data-testid="drill-in-back-btn" className="mr-1 -ml-2">
                <ChevronLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="details-content">
          {/* Drill-in view: show one employee's day-by-day breakdown */}
          {drillInEmployee ? (
            drillInLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Loading {drillInEmployee.name}'s hours...</p>
              </div>
            ) : (
              <EmployeeDaysBreakdown
                data={drillInData}
                formatDateDisplay={formatDateDisplay}
                isAdmin={isAdmin}
                onReviewed={() => drillInEmployee && openDrillIn(drillInEmployee.id, drillInEmployee.name)}
              />
            )
          ) : detailsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Fetching data...</p>
            </div>
          ) : (
            <>
              {detailsType === 'projects' && (
                <div className="projects-details space-y-4">
                  {(Array.isArray(detailsData) ? detailsData : []).map((project) => (
                    <Card key={project.id}>
                      <CardHeader>
                        <CardTitle>{project.name}</CardTitle>
                        <p className="text-sm text-gray-600">{project.address}</p>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Team Members</p>
                            <p className="text-lg font-semibold">{project.team_members?.length || 0}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Start Date</p>
                            <p className="text-lg font-semibold">{project.start_date || 'Not set'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Status</p>
                            {(() => {
                              const s = (project.status || 'active').toLowerCase();
                              const cfg = {
                                active: { variant: 'success', label: 'Active' },
                                completed: { variant: 'secondary', label: 'Completed' },
                                future: { variant: 'outline', label: 'Future' },
                                on_hold: { variant: 'warning', label: 'On hold' },
                                cancelled: { variant: 'destructive', label: 'Cancelled' },
                              }[s] || { variant: 'default', label: project.status || 'Active' };
                              return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
                            })()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!Array.isArray(detailsData) || detailsData.length === 0) && (
                    <p className="text-center text-gray-500">No active projects</p>
                  )}
                </div>
              )}

              {detailsType === 'clocked-in' && (
                <div className="clocked-in-details space-y-3">
                  {(Array.isArray(detailsData) ? detailsData : []).map((member, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{member.name}</p>
                            <p className="text-sm text-gray-600">{member.position}</p>
                            <p className="text-sm text-gray-500">
                              Clocked in at {new Date(member.clock_in).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-gray-500">Location: {member.location}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="success">Working</Badge>
                            <p className="text-sm font-semibold mt-2">{member.hours_so_far} hrs</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!Array.isArray(detailsData) || detailsData.length === 0) && (
                    <p className="text-center text-gray-500">No team members currently clocked in</p>
                  )}
                </div>
              )}

              {detailsType === 'weekly-hours' && (
                <div className="weekly-hours-details space-y-4">
                  {(Array.isArray(detailsData) ? detailsData : []).map((projectData, index) => {
                    const empId = projectData?.employee_id || projectData?.employee_breakdown?.[0]?.employee_id;
                    const empName = projectData?.project_name || 'Unknown';
                    const clickable = !!empId;
                    return (
                      <Card
                        key={index}
                        className={clickable ? 'cursor-pointer hover:shadow-lg hover:border-green-400 transition' : ''}
                        onClick={clickable ? () => openDrillIn(empId, empName) : undefined}
                        data-testid={clickable ? `weekly-emp-card-${empId}` : undefined}
                      >
                        <CardHeader className="flex flex-row justify-between items-center">
                          <CardTitle className="text-lg">{empName}</CardTitle>
                          {clickable && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              View days <ChevronRight className="h-3 w-3" />
                            </span>
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded">
                            <div>
                              <p className="text-sm text-gray-600">Total Hours</p>
                              <p className="text-xl font-bold">{projectData?.total_hours ?? 0} hrs</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Total Cost</p>
                              <p className="text-xl font-bold text-green-600">${projectData?.total_cost ?? 0}</p>
                            </div>
                          </div>
                          {Array.isArray(projectData?.employee_breakdown) && projectData.employee_breakdown.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold mb-2">Employee Breakdown:</p>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-2">Employee</th>
                                    <th className="text-right py-2">Hours</th>
                                    <th className="text-right py-2">Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {projectData.employee_breakdown.map((emp, empIdx) => (
                                    <tr key={empIdx} className="border-b">
                                      <td className="py-2">{emp?.name || 'Unknown'}</td>
                                      <td className="text-right">{emp?.hours ?? 0} hrs</td>
                                      <td className="text-right">${emp?.cost ?? 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  {(!Array.isArray(detailsData) || detailsData.length === 0) && (
                    <p className="text-center text-gray-500">No hours logged this week</p>
                  )}
                </div>
              )}

              {detailsType === 'biweekly-hours' && (
                <div className="biweekly-hours-details space-y-4">
                  {detailsData?.period && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-blue-600 font-medium">
                        Pay Period: {new Date(detailsData.period.start).toLocaleDateString()} - {new Date(detailsData.period.end).toLocaleDateString()}
                      </p>
                      <p className="text-2xl font-bold text-blue-800">{detailsData.total_hours?.toFixed(2)} Total Hours</p>
                    </div>
                  )}
                  {(detailsData?.employees || []).map((emp, index) => {
                    const clickable = !!emp?.employee_id;
                    return (
                      <Card
                        key={index}
                        className={clickable ? 'cursor-pointer hover:shadow-lg hover:border-blue-400 transition' : ''}
                        onClick={clickable ? () => openDrillIn(emp.employee_id, emp.name) : undefined}
                        data-testid={clickable ? `biweekly-emp-card-${emp.employee_id}` : undefined}
                      >
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold flex items-center gap-2">
                                {emp.name}
                                {clickable && <ChevronRight className="h-3 w-3 text-blue-500" />}
                              </p>
                              <p className="text-sm text-gray-500">{emp.role || 'Employee'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold">{emp.hours?.toFixed(2)} hrs</p>
                              <p className="text-sm text-green-600">${emp.cost?.toFixed(2)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {(!detailsData?.employees || detailsData.employees.length === 0) && (
                    <p className="text-center text-gray-500">No hours logged in this pay period</p>
                  )}
                </div>
              )}

              {detailsType === 'biweekly-payroll' && (
                <div className="biweekly-payroll-details space-y-4">
                  {detailsData?.period && (
                    <div className="bg-green-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-green-600 font-medium">
                        Pay Period: {new Date(detailsData.period.start).toLocaleDateString()} - {new Date(detailsData.period.end).toLocaleDateString()}
                      </p>
                      <p className="text-2xl font-bold text-green-800">${detailsData.total_pay?.toFixed(2)} Total Payroll</p>
                    </div>
                  )}
                  {(detailsData?.employees || []).map((emp, index) => {
                    const clickable = !!emp?.employee_id;
                    return (
                      <Card
                        key={index}
                        className={clickable ? 'cursor-pointer hover:shadow-lg hover:border-green-400 transition' : ''}
                        onClick={clickable ? () => openDrillIn(emp.employee_id, emp.name) : undefined}
                        data-testid={clickable ? `payroll-emp-card-${emp.employee_id}` : undefined}
                      >
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold flex items-center gap-2">
                                {emp.name}
                                {clickable && <ChevronRight className="h-3 w-3 text-green-500" />}
                              </p>
                              <p className="text-sm text-gray-500">${emp.hourly_rate?.toFixed(2)}/hr × {emp.hours?.toFixed(2)} hrs</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-green-600">${emp.cost?.toFixed(2)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {(!detailsData?.employees || detailsData.employees.length === 0) && (
                    <p className="text-center text-gray-500">No payroll data for this period</p>
                  )}
                </div>
              )}

              {detailsType === 'open-orders' && (
                <div className="open-orders-details space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Badge variant="warning">Pending Orders</Badge>
                    </h3>
                    {materialOrders.filter(o => o.status === 'pending' || o.status === 'created').length === 0 ? (
                      <p className="text-gray-500 italic text-center py-4">No pending orders</p>
                    ) : (
                      <div className="space-y-2">
                        {materialOrders.filter(o => o.status === 'pending' || o.status === 'created').map((order) => {
                          const project = projects.find(p => p.id === order.project_id);
                          return (
                            <Card key={order.id} className="cursor-pointer hover:shadow-md" onClick={() => onViewOrderDetails(order)}>
                              <CardContent className="pt-4">
                                <div className="flex justify-between">
                                  <span className="font-semibold">{project?.name || 'Unknown'}</span>
                                  <span className="text-sm text-gray-500">{new Date(order.order_date).toLocaleDateString()}</span>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(detailsType === 'personal-weekly-hours' || detailsType === 'personal-biweekly-hours' || detailsType === 'personal-biweekly-payroll') && (
                <EmployeeDaysBreakdown data={detailsData} formatDateDisplay={formatDateDisplay} isAdmin={false} />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default StatsDetailsDialog;
